/**
 * Calculates the mean of an array of numbers.
 */
export const getMean = (array: number[]) => array.reduce((a, b) => a + b) / array.length;

/**
 * Calculates the standard deviation of an array of numbers.
 */
export const getStandardDeviation = (array: number[]) => {
  const mean = getMean(array);
  return Math.sqrt(array.map((x) => Math.pow(x - mean, 2)).reduce((a, b) => a + b) / array.length);
};

export type IUnit = "centimetres" | "inches" | "feet" | "metres" | "yards";
type IConversionFactor = {
  [key in IUnit]: {
    [subKey in IUnit]?: number;
  };
};

export const Fixed = (val: number) => {
  return Math.round(val * 100) / 100;
};

export function convertLength<FromUnit extends IUnit, ToUnit extends IUnit, ShowUnits extends undefined>(
  value: number,
  fromUnit: FromUnit,
  toUnit: ToUnit extends FromUnit ? null : ToUnit, // units must be different
  showUnits?: ShowUnits
): number;
export function convertLength<FromUnit extends IUnit, ToUnit extends IUnit, ShowUnits extends boolean>(
  value: number,
  fromUnit: FromUnit,
  toUnit: ToUnit extends FromUnit ? null : ToUnit, // units must be different
  showUnits?: ShowUnits
): ShowUnits extends true ? string : number;
export function convertLength<FromUnit extends IUnit, ToUnit extends IUnit>(
  value: number,
  fromUnit: FromUnit,
  toUnit: ToUnit extends FromUnit ? null : ToUnit, // units must be different
  showUnits = false
): string | number {
  // Get the conversion factors
  const conversionFactors: IConversionFactor = {
    centimetres: {
      feet: 0.032808399,
      inches: 0.3937007874,
      metres: 0.01,
      yards: 0.010936133,
    },
    inches: {
      centimetres: 2.54,
      feet: 0.0833333333,
      metres: 0.0254,
      yards: 0.0277777778,
    },
    feet: {
      centimetres: 30.48,
      inches: 12,
      metres: 0.3048,
      yards: 0.333333333,
    },
    metres: {
      centimetres: 100,
      feet: 3.280839895,
      inches: 39.37007874,
      yards: 1.093613299,
    },
    yards: {
      centimetres: 91.44,
      feet: 3,
      inches: 36,
      metres: 0.9144,
    },
  };

  // Calculate the converted value
  const convertedValue = value * conversionFactors[fromUnit][toUnit];

  return showUnits ? `${convertedValue}${toUnit}` : convertedValue;
}